import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import Navbar from "../components/common/Navbar";
import Footer from "../components/common/Footer";
import ScrollReveal from "../components/common/ScrollReveal";
import styles from "./MenuPage.module.css";

function MenuPage() {
  const [menuStructure, setMenuStructure] = useState([]);
  const [activeMainCategory, setActiveMainCategory] = useState("");
  const [activeSubCategory, setActiveSubCategory] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchMenuData();
  }, []);

  const fetchMenuData = async () => {
    try {
      setIsLoading(true);

      const [catRes, prodRes] = await Promise.all([
        fetch("/api/categories?type=Menu"),
        fetch("/api/cms/products"),
      ]);

      const categoriesData = await catRes.json();
      const allProducts = await prodRes.json();

      const menuProducts = allProducts.filter(
        (p) => p.category && p.category.type === "Menu"
      );

      const structure = [
        { id: "Drinks", title: "Drinks", subcategories: [] },
        { id: "Food", title: "Food", subcategories: [] },
      ];

      categoriesData.forEach((cat) => {
        const productsInCat = menuProducts.filter(
          (p) => p.category._id === cat._id
        );

        let groupName = "Food";

        if (cat.menuSection === "Drink") {
          groupName = "Drinks";
        } else if (cat.menuSection === "Food") {
          groupName = "Food";
        } else {
          const lowerName = cat.name.toLowerCase();
          if (
            lowerName.includes("coffee") ||
            lowerName.includes("juice") ||
            lowerName.includes("tea") ||
            lowerName.includes("smoothie")
          ) {
            groupName = "Drinks";
          }
        }

        const groupIndex = structure.findIndex((g) => g.title === groupName);

        if (groupIndex !== -1) {
          structure[groupIndex].subcategories.push({
            id: cat._id,
            title: cat.name,
            image: cat.image,
            data: productsInCat,
            isFood: groupName === "Food",
          });
        }
      });

      /**
       * 💎 1. CUSTOM SORTING LOGIC: "Filter Coffee" Priority
       * This ensures Filter Coffee is always the first subcategory in Drinks.
       */
      const drinksGroup = structure.find((g) => g.id === "Drinks");
      if (drinksGroup) {
        drinksGroup.subcategories.sort((a, b) => {
          const aTitle = a.title.toLowerCase().trim();
          const bTitle = b.title.toLowerCase().trim();

          if (aTitle === "filter coffee") return -1;
          if (bTitle === "filter coffee") return 1;
          return 0;
        });
      }

      const finalStructure = structure.filter(
        (g) => g.subcategories.length > 0
      );
      setMenuStructure(finalStructure);

      if (finalStructure.length > 0) {
        const urlMain = searchParams.get("category");
        const urlSub = searchParams.get("subcategory");

        const foundMain = finalStructure.find((g) => g.title === urlMain);
        const foundSub = foundMain?.subcategories.find(
          (s) => s.id === urlSub || s.title === urlSub
        );

        if (foundMain && foundSub) {
          setActiveMainCategory(foundMain.id);
          setActiveSubCategory(foundSub.id);
        } else {
          setActiveMainCategory(finalStructure[0].id);
          if (finalStructure[0].subcategories.length > 0) {
            setActiveSubCategory(finalStructure[0].subcategories[0].id);
          }
        }
      }
    } catch (error) {
      console.error("Error loading menu:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMainCategoryClick = (mainId, mainTitle) => {
    setActiveMainCategory(mainId);
    const group = menuStructure.find((g) => g.id === mainId);
    if (group && group.subcategories.length > 0) {
      const firstSub = group.subcategories[0];
      setActiveSubCategory(firstSub.id);
      setSearchParams({ category: mainTitle, subcategory: firstSub.title });
    }
  };

  const handleSubCategoryClick = (subId, subTitle) => {
    const parentGroup = menuStructure.find((group) =>
      group.subcategories.some((sub) => sub.id === subId)
    );

    if (parentGroup) {
      setActiveMainCategory(parentGroup.id);
      setActiveSubCategory(subId);
      setSearchParams({ category: parentGroup.title, subcategory: subTitle });
    }
  };

  const formatPrice = (item) => {
    if (item.price && String(item.price).trim() !== "" && item.price !== 0) {
      return `${item.price} Br`;
    }
    return "";
  };

  const allSubcategories = menuStructure.flatMap((main) => main.subcategories);
  const activeGroup = menuStructure.find((g) => g.id === activeMainCategory);
  const activeData = activeGroup?.subcategories.find(
    (sub) => sub.id === activeSubCategory
  );

  const getBackgroundImage = (path) => {
    if (!path) return {};
    const cleanPath = path.replace(/\\/g, "/");
    return {
      backgroundImage: `linear-gradient(rgba(0,0,0,0.2), rgba(0,0,0,0.5)), url(${cleanPath})`,
      backgroundSize: "cover",
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat",
      boxShadow: "inset 0 0 100px 80px #050505",
      border: "none",
      borderRadius: "20px",
      padding: "60px",
      marginBottom: "60px",
      minHeight: "500px",
    };
  };

  return (
    <div className={styles.menuPageContainer}>
      <Navbar />

      <section className={styles.menuBanner}>
        <h1 className={styles.menuBannerTitle}>Menu</h1>
      </section>

      <div className={styles.topMainCategoryNav}>
        {menuStructure.map((mainCat) => (
          <button
            key={mainCat.id}
            className={`${styles.mainCategoryButton} ${
              activeMainCategory === mainCat.id ? styles.active : ""
            }`}
            onClick={() => handleMainCategoryClick(mainCat.id, mainCat.title)}
          >
            {mainCat.title}
          </button>
        ))}
      </div>

      <div className={styles.menuContentWrapper}>
        <nav className={styles.sideNavigation}>
          <div className={styles.subCategoryList}>
            {activeGroup?.subcategories.map((subCat) => (
              <button
                key={subCat.id}
                className={`${styles.subCategoryButton} ${
                  activeSubCategory === subCat.id ? styles.active : ""
                }`}
                onClick={() => handleSubCategoryClick(subCat.id, subCat.title)}
              >
                {subCat.title}
              </button>
            ))}
          </div>
        </nav>

        <main className={styles.mainContent}>
          <div className={styles.mobileTabWrapper}>
            {allSubcategories.map((subCat) => (
              <button
                key={subCat.id}
                className={`${styles.mobileTabButton} ${
                  activeSubCategory === subCat.id ? styles.active : ""
                }`}
                onClick={() => handleSubCategoryClick(subCat.id, subCat.title)}
              >
                {subCat.title}
              </button>
            ))}
          </div>

          {isLoading ? (
            <p style={{ padding: 20 }}>Loading menu...</p>
          ) : (
            activeData && (
              <>
                <ScrollReveal>
                  <h3 className={styles.subCategoryTitle}>
                    {activeData.title}
                  </h3>
                </ScrollReveal>

                <div
                  className={styles.activeContentSection}
                  style={getBackgroundImage(activeData.image)}
                >
                  <div className={styles.menuGrid}>
                    {activeData.data.length === 0 ? (
                      <p style={{ color: "#fff", fontStyle: "italic" }}>
                        Coming soon...
                      </p>
                    ) : (
                      activeData.data.map((item, index) => {
                        const formattedPrice = formatPrice(item);
                        return (
                          <ScrollReveal
                            key={item._id}
                            className={styles.menuItemCard}
                            delay={index * 0.05}
                          >
                            {item.imageURL && (
                              <img
                                src={`${item.imageURL}`}
                                alt={item.name}
                                className={styles.menuItemImage}
                              />
                            )}

                            <div className={styles.menuItemInfo}>
                              <h4>{item.name}</h4>
                              {item.description && (
                                <p className={styles.itemDescription}>
                                  {item.description}
                                </p>
                              )}
                              {formattedPrice !== "" && (
                                <span className={styles.itemPrice}>
                                  {formattedPrice}
                                </span>
                              )}
                            </div>
                          </ScrollReveal>
                        );
                      })
                    )}
                  </div>
                </div>
              </>
            )
          )}
        </main>
      </div>
      <Footer />
    </div>
  );
}

export default MenuPage;
